import { listClasses } from '@mui/material/List';
import { menuItemClasses } from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';

import { Arrow } from './styles';
import type { CustomPopoverProps } from './types';
import { calculateAnchorOrigin } from './utils';

/**
 * A customized version of Material-UI's Popover component that includes an arrow.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Controls whether the popover is displayed
 * @param {Function} props.onClose - Callback fired when the popover is requested to be closed
 * @param {React.ReactNode} props.children - The content of the popover
 * @param {Element | null} props.anchorEl - The DOM element used to set the position of the popover
 * @param {Object} [props.slotProps] - Props applied to the slot elements
 * @param {Object} [props.slotProps.arrow] - Props applied to the arrow element
 * @param {number} [props.slotProps.arrow.size=14] - Size of the arrow in pixels
 * @param {number} [props.slotProps.arrow.offset=17] - Offset position of the arrow
 * @param {string} [props.slotProps.arrow.placement='top-right'] - Placement of the arrow ('top-right', 'top-left', etc.)
 * @param {boolean} [props.slotProps.arrow.hide] - Whether to hide the arrow
 * @param {Object} [props.slotProps.arrow.sx] - The system prop that allows defining system overrides for the arrow
 * @param {Object} [props.slotProps.paper] - Props applied to the Paper component
 * @param {Object} [props.slotProps.paper.sx] - The system prop that allows defining system overrides for the paper
 * @returns {JSX.Element} - The CustomPopover component
 */
export function CustomPopover({
    open,
    onClose,
    children,
    anchorEl,
    slotProps,
    ...other
}: CustomPopoverProps) {
    const { arrow: arrowProps, paper: paperProps, ...otherSlotProps } = slotProps ?? {};

    const arrowSize = arrowProps?.size ?? 14;
    const arrowOffset = arrowProps?.offset ?? 17;
    const arrowPlacement = arrowProps?.placement ?? 'top-right';

    const { paperStyles, anchorOrigin, transformOrigin } = calculateAnchorOrigin(arrowPlacement);

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            slotProps={{
                ...otherSlotProps,
                paper: {
                    ...paperProps,
                    sx: [
                        paperStyles,
                        {
                            overflow: 'inherit',
                            [`& .${listClasses.root}`]: { minWidth: 140 },
                            [`& .${menuItemClasses.root}`]: { gap: 2 },
                        },
                        ...(Array.isArray(paperProps?.sx)
                            ? paperProps?.sx ?? []
                            : [paperProps?.sx]),
                    ],
                },
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {!arrowProps?.hide && (
                <Arrow
                    size={arrowSize}
                    offset={arrowOffset}
                    placement={arrowPlacement}
                    sx={arrowProps?.sx}
                />
            )}

            {children}
        </Popover>
    );
}
